import { useCallback, useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";

import { ImportJobResult, ImportJobStatus } from "./import";
import { importAtom } from "./import.state";
import {
  useContinueHandler,
  useFinishHandler,
} from "./import.handlers";
import { trackEvent } from "../analytics/analytics";
import apiClient from "../../services/apiClient";
import { devWarn } from "../../services/util";

export type ImportJobParams = {
  onFinish: (status: ImportJobStatus, result: ImportJobResult) => void;
  onContinue?: (status: ImportJobStatus) => void;
  serviceJobId: string;
  /** Reserved for parity with the previous API; ignored under the session-scoped poller (DB refresh always happens). */
  skipRefresh?: boolean;
};

/**
 * Thin facade over the session-scoped `ImportPoller` component.
 *
 * Pre-Apr-2026: this hook owned its own polling lifecycle inside the import
 * page's component tree, so navigating away mid-import lost the `finish` event
 * (Mode C). We also had a `currentJob` guard that silently swallowed `start`
 * events on resume (Mode B). Both are gone — the global poller handles
 * lifecycle, and consumer-specific reactions register via `useFinishHandler`.
 */
export const useImportJob = ({
  serviceJobId,
  onFinish,
  onContinue,
}: ImportJobParams) => {
  const [importState, setImportState] = useAtom(importAtom);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);

  // Register consumer callbacks so the poller can reach them across navigation.
  useFinishHandler(serviceJobId, async (status, result) => {
    setImporting(false);
    setImported(true);
    onFinish?.(status, result);
  });
  useContinueHandler(serviceJobId, onContinue);

  // One-time mount fetch — primes lastJobStatus.checked so consumers (CSV modal's
  // "Preparing job..." gating, Shopify's "previously imported" line) resolve
  // immediately on page load. Without it `checked` stays false forever when
  // the atom hasn't seen a job complete this session. If an in-flight job is
  // found server-side, also primes currentJob so the session-scoped poller
  // picks up tracking. Skip when the atom already has a current job for this
  // serviceJobId — poller is already polling.
  const hasCheckedRef = useRef(false);
  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { data: status } = await apiClient.get<ImportJobStatus>(
          "/data/job-status?jobId=" + serviceJobId,
          { timeout: 10000 },
        );
        if (cancelled) return;
        setImportState((s) => {
          // Don't clobber an active job already being polled.
          if (s.currentJob?.serviceJobId === serviceJobId) {
            return { ...s, checked: true };
          }
          if (status?.job && !status.job.finished) {
            // Active job server-side that we didn't know about — pick it up.
            return {
              ...s,
              currentJob: {
                jobId: status.id,
                serviceJobId,
                name: status.job.name,
              },
              jobStatus: status,
              checked: true,
            };
          }
          // Finished job (or no job) — surface as last status if present.
          return {
            ...s,
            lastJobStatus: status?.job ? status : s.lastJobStatus,
            checked: true,
          };
        });
      } catch (error) {
        // Non-fatal — flip checked anyway so consumers don't hang on
        // "Preparing job..." indefinitely on a network blip.
        devWarn("Initial job status fetch failed:", error);
        if (!cancelled) {
          setImportState((s) => ({ ...s, checked: true }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceJobId]);

  const isCurrent = importState.currentJob?.serviceJobId === serviceJobId;
  const jobStatus = isCurrent ? importState.jobStatus : undefined;

  // lastJobStatus per-consumer: only surface the most recent finish if it was ours.
  const lastJobStatus = importState.lastJobStatus?.id?.startsWith(serviceJobId)
    ? { checked: true, status: importState.lastJobStatus }
    : { checked: importState.checked, status: undefined };

  const startJob = useCallback(
    async (jobId: string, name?: string, count?: number) => {
      trackEvent({
        category: serviceJobId,
        action: "start",
        label: name,
        value: count,
        feature: !!name,
      });
      setImporting(true);
      setImported(false);
      setImportState((s) => ({
        ...s,
        currentJob: { jobId, serviceJobId, name, productCount: count },
        jobStatus: undefined,
      }));
    },
    [serviceJobId, setImportState],
  );

  const resetJob = useCallback(() => {
    setImportState((s) => ({ ...s, jobStatus: undefined }));
    setImporting(false);
  }, [setImportState]);

  const stopJob = useCallback(async (jobId: string) => {
    const response = await apiClient.post("/data/stop-job?jobId=" + jobId);
    return response.data;
  }, []);

  return {
    startJob,
    jobStatus,
    resetJob,
    stopJob,
    lastJobStatus,
    importing: importing || (isCurrent && !!importState.currentJob),
    setImporting,
    imported,
    setImported,
  };
};
